import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import {
  emptyPromoCrops,
  MINI_MENU_PROMO_FRAMES,
  MiniMenuPromo,
  MiniMenuPromoCrops,
  MiniMenuPromoStatus
} from 'src/app/services/mini-menu.service';
import {
  downscaleImageFile,
  DownscaleResult,
  formatBytes,
  UPLOAD_MAX_BYTES,
  UPLOAD_MAX_DECODE_BYTES
} from 'src/app/common/image-downscale';

/**
 * Promotion (What's On) editor modal. The restaurant writes its own promotions here —
 * every field is authored in this form, nothing is pulled from office Foodie News.
 *
 * Mirrors the field layout of the Add-Dish modal (app-menu) and the 3-tab cropper flow of
 * ImageConfigurationComponent, retargeted to the three MiniMenu promo device frames
 * (mobile / tablet-landscape / tablet-portrait).
 *
 * Status is shown but never edited: Save keeps the promotion a draft, and publishing goes
 * through the approval queue from the What's On list — the same shape as Save Details then
 * Publish on the business form.
 */
@Component({
  selector: 'app-mini-menu-promo-modal',
  templateUrl: './mini-menu-promo-modal.component.html',
  styleUrls: ['./mini-menu-promo-modal.component.scss']
})
export class MiniMenuPromoModalComponent implements OnInit {
  // Set by the opener before the modal renders.
  promo!: MiniMenuPromo;
  isNew = true;

  readonly frames = MINI_MENU_PROMO_FRAMES;

  imageSource: string | null = null;
  isImageReady = false;
  /** True while a picked file is being downscaled, so the picker can say so. */
  isCompressing = false;
  // Kept in sync by the crop editor's cropsChange, so save() never has to reach into it.
  crops: MiniMenuPromoCrops = emptyPromoCrops();
  // The chosen file, handed back to the opener so it can upload it after the modal closes.
  // A data-URL preview alone cannot be POSTed as multipart.
  private pendingImageFile: File | null = null;

  formGroup = new FormGroup({
    Badge: new FormControl(''),
    Title: new FormControl('', Validators.required),
    When: new FormControl(''),
    Lead: new FormControl(''),
    Body: new FormControl('')
  });

  constructor(private activeModal: NgbActiveModal, private toastr: ToastrService) {}

  ngOnInit(): void {
    if (!this.promo) {
      this.promo = {
        id: 'promo-new',
        promotionId: 0,
        badge: '', title: '', when: '', lead: '', body: '',
        status: 'draft', image: null, crops: emptyPromoCrops(), sortOrder: 0
      };
    }
    this.formGroup.patchValue({
      Badge: this.promo.badge,
      Title: this.promo.title,
      When: this.promo.when,
      Lead: this.promo.lead,
      Body: this.promo.body
    });
    this.imageSource = this.promo.image;
    this.isImageReady = !!this.promo.image;
    this.crops = {
      mobile: this.promo.crops?.mobile || null,
      tablet: this.promo.crops?.tablet || null,
      tabletPortrait: this.promo.crops?.tabletPortrait || null
    };
  }

  /** Read-only badge in the header. The restaurant never picks its own approval state. */
  get statusLabel(): string {
    const labels: Record<MiniMenuPromoStatus, string> = {
      draft: 'Draft',
      review: 'Pending review',
      live: 'Published',
      promoted: 'Promoted',
      suspended: 'Suspended',
      deleted: 'Deleted'
    };
    return labels[this.promo?.status] || 'Draft';
  }

  get statusHint(): string {
    switch (this.promo?.status) {
      case 'review':
        return 'This promotion is waiting for HUNGERY approval. Saving edits keeps it in the queue.';
      case 'live':
      case 'promoted':
        return 'This promotion is live. Saved edits go back for approval before the public menu updates.';
      case 'suspended':
        return 'This promotion is hidden from the public menu. Saving edits keeps it hidden.';
      default:
        return 'Saving keeps this promotion a draft. You choose whether to submit it for review afterwards.';
    }
  }

  async onImageSelected(event: Event): Promise<void> {
    if (this.isCompressing) {
      return;
    }
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) {
      return;
    }
    if (!file.type.startsWith('image/')) {
      this.toastr.error('Please select an image.');
      return;
    }
    if (file.size > UPLOAD_MAX_DECODE_BYTES) {
      this.toastr.error(`Please pick an image under ${formatBytes(UPLOAD_MAX_DECODE_BYTES)}.`);
      return;
    }

    // Shrink BEFORE the cropper opens: crop rectangles are stored in the uploaded
    // image's pixel space, and Evolution serves back whatever it was given.
    this.isCompressing = true;
    const prepared: DownscaleResult = await downscaleImageFile(file);
    if (prepared.file.size > UPLOAD_MAX_BYTES) {
      this.isCompressing = false;
      this.toastr.error(`Please pick an image under ${formatBytes(UPLOAD_MAX_BYTES)}.`);
      return;
    }

    this.pendingImageFile = prepared.file;
    const reader = new FileReader();
    reader.onload = () => {
      this.imageSource = String(reader.result || '');
      this.crops = emptyPromoCrops();
      this.isImageReady = false;
      this.isCompressing = false;
      // Let the crop editor mount against the fresh source.
      setTimeout(() => { this.isImageReady = true; }, 0);
    };
    reader.onerror = () => {
      this.pendingImageFile = null;
      this.isCompressing = false;
      this.toastr.error('The selected image could not be prepared.');
    };
    reader.readAsDataURL(prepared.file);
  }

  removeImage(): void {
    this.imageSource = null;
    this.isImageReady = false;
    this.crops = emptyPromoCrops();
    this.pendingImageFile = null;
  }

  /** The crop editor emits on every drag, so the latest rects are always here by save time. */
  onCropsChange(crops: MiniMenuPromoCrops): void {
    this.crops = crops;
  }

  save(): void {
    if (this.isCompressing) {
      return;
    }
    this.formGroup.markAllAsTouched();
    if (this.formGroup.invalid) {
      this.toastr.error('Title is required.');
      return;
    }
    const result: MiniMenuPromo = {
      ...this.promo,
      badge: this.formGroup.get('Badge')?.value || '',
      title: this.formGroup.get('Title')?.value || '',
      when: this.formGroup.get('When')?.value || '',
      lead: this.formGroup.get('Lead')?.value || '',
      body: this.formGroup.get('Body')?.value || '',
      // Editing never publishes. The status the promotion came in with is preserved here;
      // the What's On list decides whether a saved edit has to re-enter the approval queue.
      status: this.promo.status,
      image: this.imageSource,
      pendingImageFile: this.pendingImageFile,
      crops: {
        mobile: this.imageSource ? this.crops.mobile : null,
        tablet: this.imageSource ? this.crops.tablet : null,
        tabletPortrait: this.imageSource ? this.crops.tabletPortrait : null
      }
    };
    this.activeModal.close(result);
  }

  cancel(): void {
    this.activeModal.dismiss('cancel');
  }
}
